import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, Channel, ChannelAnalyticsSummary, DbQuota, NeonStatus, PlatformService, PlatformStatus, SupabaseStatus, UploadStartResult, UploadStatus } from '../lib/api'
import HudLoader from '../components/HudLoader'
import {
  Youtube,
  Video,
  FileText,
  Briefcase,
  TrendingUp,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  ShieldCheck,
  BadgeDollarSign,
  Database,
  Server,
  Globe,
  Github,
} from 'lucide-react'
import { format, subDays } from 'date-fns'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts'

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899']

function StatCard({ title, value, change, icon: Icon, color }: {
  title: string
  value: string | number
  change?: { value: number; label: string }
  icon: React.ComponentType<any>
  color: string
}) {
  return (
    <div className="stat-card">
      <div className="stat-header">
        <div className="stat-icon" style={{ backgroundColor: `${color}15`, color }}>
          <Icon size={24} />
        </div>
        {change && (
          <div className={`stat-change ${change.value >= 0 ? 'positive' : 'negative'}`}>
            <span>{change.value >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}</span>
            <span>{Math.abs(change.value)}%</span>
            <span className="change-label">{change.label}</span>
          </div>
        )}
      </div>
      <div className="stat-value">{value}</div>
      <div className="stat-title">{title}</div>
    </div>
  )
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="chart-card">
      <h3>{title}</h3>
      <div className="chart-container">{children}</div>
    </div>
  )
}

function MonetizationCard({ channel }: { channel: Channel }) {
  const { data: analytics } = useQuery({
    queryKey: ['analytics', 'channel', channel.id, 365],
    queryFn: () => api.get<ChannelAnalyticsSummary>(`/analytics/channel/${channel.id}/summary?days=365`),
  })
  const subs = channel.subscriber_count || 0
  const hours = (analytics?.total_watch_time_minutes || 0) / 60
  const subsPct = Math.min(100, (subs / 1000) * 100)
  const hoursPct = Math.min(100, (hours / 4000) * 100)
  const eligible = channel.ypp_eligible && subs >= 1000 && hours >= 4000
  return (
    <div className="card">
      <div className="card-header">
        <h2><BadgeDollarSign size={16} /> Road to Monetization — {channel.title}</h2>
        {eligible
          ? <span className="badge badge-success">Ready to apply in YouTube Studio!</span>
          : <span className="badge badge-warning">In progress</span>}
      </div>
      <div className="form-group">
        <label>Subscribers: {subs.toLocaleString()} / 1,000 ({subsPct.toFixed(1)}%)</label>
        <div className="progress-bar"><div className="progress-fill" style={{ width: `${subsPct}%` }} /></div>
      </div>
      <div className="form-group">
        <label>Watch hours (12 months): {hours.toFixed(0)} / 4,000 ({hoursPct.toFixed(1)}%)</label>
        <div className="progress-bar"><div className="progress-fill" style={{ width: `${hoursPct}%` }} /></div>
      </div>
      <p className="muted">Alternative: 10M Shorts views in 90 days — Shorts engine is ON. Keep publishing + sync analytics (Channels → sync icon) for fresh numbers.</p>
    </div>
  )
}

function fmtBytes(n?: number | null): string {
  if (n == null) return '—'
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(2)} GB`
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(1)} MB`
  if (n >= 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${n} B`
}

function QuotaBar({ quota, label }: { quota?: DbQuota; label: string }) {
  // Free-plan quota: total vs abhi use. used_bytes null = pata nahi
  // (Neon quota out → DB query hi nahi chalti).
  if (!quota?.limit_bytes) return null
  const pct = quota.used_bytes != null
    ? Math.min(100, (quota.used_bytes / quota.limit_bytes) * 100)
    : null
  const color = pct == null ? '#f59e0b' : pct > 90 ? '#ef4444' : pct > 70 ? '#f59e0b' : '#10b981'
  return (
    <div className="quota-row">
      <div className="quota-line">
        <span>{label}</span>
        <span className="quota-num">
          {fmtBytes(quota.used_bytes)} / {fmtBytes(quota.limit_bytes)}
          {pct != null ? ` · ${pct < 1 ? pct.toFixed(2) : pct.toFixed(1)}%` : ''}
        </span>
      </div>
      <div className="quota-track" title={quota.note || undefined}>
        <div className="quota-fill" style={{ width: `${pct ?? 3}%`, background: color }} />
      </div>
      {quota.compute_limit_hours ? (
        <div className="quota-extra">
          Compute quota: {quota.compute_used_hours ?? '—'} / {quota.compute_limit_hours} CU-hours/month
          {quota.compute_used_hours == null ? ' (reset 1 Nov)' : ''}
        </div>
      ) : null}
    </div>
  )
}

function SupabaseDbCard() {
  // Primary (live) DB ka status — ek baar check (poll nahi).
  const { data: sb, isLoading } = useQuery({
    queryKey: ['supabase-status'],
    queryFn: () => api.get<SupabaseStatus>('/metrics/supabase'),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })
  if (isLoading) return null
  const ok = !!sb?.db_ok
  const color = ok ? '#10b981' : '#ef4444'
  const title = ok
    ? `${sb?.live_usage_note} · ${sb?.host || 'supabase.com'}${sb?.latency_ms != null ? ` · ${sb.latency_ms}ms` : ''}`
    : sb?.live_usage_note || 'DB jawab nahi de rahi'
  return (
    <div className="stat-card" title={title}>
      <div className="stat-header">
        <div className="stat-icon" style={{ backgroundColor: `${color}15`, color }}>
          <Database size={24} />
        </div>
        <span className="service-name">Supabase</span>
        {ok
          ? <span className="badge badge-success">Live</span>
          : <span className="badge badge-danger">Down</span>}
      </div>
      <div className="stat-value" style={{ fontSize: 16 }}>
        {ok
          ? `Primary${sb?.latency_ms != null ? ` · ${sb.latency_ms}ms` : ''}`
          : (sb?.role || 'Error')}
      </div>
      <div className="stat-title">
        {ok ? (sb?.region || 'primary') : 'check console'} ·{' '}
        <a href={sb?.console_url || 'https://supabase.com/dashboard'} target="_blank" rel="noopener noreferrer">Console</a>
      </div>
      <QuotaBar quota={sb?.quota} label="Storage" />
      {/* Auto-quota: hourly guard + daily prune/VACUUM — koi manual kaam nahi */}
      <div className="quota-extra" title={sb?.retention?.vacuum || undefined}>
        {sb?.guard?.used_pct != null
          ? `Guard: ${sb.guard.used_pct}% used · auto-prune at ${sb.guard.threshold_pct ?? 85}%`
          : 'Guard: hourly usage check ON'}
        {sb?.retention?.ran_at
          ? ` · Prune ${sb.retention.ran_at.slice(11, 16)} UTC: ${sb.retention.total_rows ?? 0} rows, ${sb.retention.freed_bytes != null ? fmtBytes(sb.retention.freed_bytes) : '0 B'} free`
          : ''}
      </div>
    </div>
  )
}

function NeonDbCard() {
  // Ek baar check (poll nahi — har check Neon jagata hai = burn).
  const { data: neon, isLoading } = useQuery({
    queryKey: ['neon-status'],
    queryFn: () => api.get<NeonStatus>('/metrics/neon'),
    staleTime: 10 * 60 * 1000,
    retry: 1,
  })
  if (isLoading) return null
  const ok = !!neon?.db_ok
  const color = ok ? '#10b981' : '#f59e0b'
  const syncNote = neon?.backup_sync?.note
  const title = ok
    ? `${neon?.live_usage_note}${neon?.latency_ms != null ? ` · ${neon.latency_ms}ms` : ''}${syncNote ? ` · ${syncNote}` : ''}`
    : `${neon?.live_usage_note || 'Neon quota exceed'}${syncNote ? ` · ${syncNote}` : ''}`
  return (
    <div className="stat-card" title={title}>
      <div className="stat-header">
        <div className="stat-icon" style={{ backgroundColor: `${color}15`, color }}>
          <Database size={24} />
        </div>
        <span className="service-name">Neon</span>
        {ok
          ? <span className="badge badge-success">Live</span>
          : <span className="badge badge-warning">Backup</span>}
      </div>
      <div className="stat-value" style={{ fontSize: 16 }}>
        {ok
          ? `Connected${neon?.latency_ms != null ? ` · ${neon.latency_ms}ms` : ''}`
          : 'Quota out · Nov 1'}
      </div>
      <div className="stat-title">
        Backup DB ·{' '}
        <a href={ok ? neon?.console_url : 'https://console.neon.tech'} target="_blank" rel="noopener noreferrer">Console</a>
      </div>
      <QuotaBar quota={neon?.quota} label="Storage" />
    </div>
  )
}

const PLATFORM_INDICATOR: Record<string, { label: string; color: string; badge: string }> = {
  none: { label: 'Operational', color: '#10b981', badge: 'badge-success' },
  minor: { label: 'Degraded', color: '#f59e0b', badge: 'badge-warning' },
  major: { label: 'Major outage', color: '#ef4444', badge: 'badge-danger' },
  critical: { label: 'Outage', color: '#ef4444', badge: 'badge-danger' },
  maintenance: { label: 'Maintenance', color: '#3b82f6', badge: 'badge-warning' },
  unknown: { label: 'Status unknown', color: '#94a3b8', badge: 'badge-warning' },
}

function PlatformCard({ svc }: { svc: PlatformService }) {
  // Compact (DB cards jaisa): icon+badge, ek chhoti value line, naam+links,
  // optional usage bar, aur ek line me free-plan limit. Poori note text
  // sirf tooltip me (card lamba nahi hota).
  const st = PLATFORM_INDICATOR[svc.indicator] || PLATFORM_INDICATOR.unknown
  const Icon = svc.id === 'render' ? Server : svc.id === 'cloudflare' ? Globe : Github
  const used = svc.quota_used
  const limit = svc.quota_limit_num
  const pct = used != null && limit ? Math.min(100, (used / limit) * 100) : null
  const barColor = pct == null ? '#f59e0b' : pct > 90 ? '#ef4444' : pct > 70 ? '#f59e0b' : '#10b981'
  return (
    <div
      className="stat-card platform-card"
      title={`${svc.description} · ${svc.quota_note} · checked ${svc.checked_at}`}
    >
      <div className="stat-header">
        <div className="stat-icon platform-icon" style={{ backgroundColor: `${st.color}15`, color: st.color }}>
          <Icon size={18} />
        </div>
        <span className="service-name">{svc.name.split(' (')[0]}</span>
        <span className={`badge ${st.badge}`}>{st.label}</span>
      </div>
      <div className="stat-value platform-value">{svc.detail || svc.description}</div>
      {/* Task 2 (2.2.36): outage/maintenance par statuspage ki asli wajah
          visible — sirf badge + "HTTP200" contradictory lagta tha. */}
      {st.badge === 'badge-danger' || st.badge === 'badge-warning' ? (
        <div className="platform-alert" style={{ color: st.color, fontSize: 11, marginTop: 2 }}>
          {svc.description}
        </div>
      ) : null}
      <div className="stat-title">
        <a href={svc.console_url} target="_blank" rel="noopener noreferrer">Console</a>
        {' · '}
        <a href={svc.status_url} target="_blank" rel="noopener noreferrer">Status</a>
      </div>
      {/* Usage bar sirf tab jab asli number mile (GitHub rate-limit API). */}
      {pct != null && used != null && limit ? (
        <div className="quota-row platform-quota">
          <div className="quota-line">
            <span>API usage</span>
            <span className="quota-num">
              {used}/{limit} {svc.quota_unit} · {pct.toFixed(0)}%
            </span>
          </div>
          <div className="quota-track">
            <div className="quota-fill" style={{ width: `${Math.max(pct, 2)}%`, background: barColor }} />
          </div>
        </div>
      ) : null}
      <div className="quota-extra platform-extra">{svc.quota_limit}</div>
    </div>
  )
}

/** 2.2.36 — Global upload kill-switch card (Task 3): PAUSED badge +
 * "Start online uploading" button (resume + pending queue flush). User
 * jab chahe tab upload trigger — preview dekh kar quality sahi lage tab. */
function UploadGateCard() {
  const qc = useQueryClient()
  const { data } = useQuery({
    queryKey: ['settings', 'upload-status'],
    queryFn: () => api.get<UploadStatus>('/settings/upload-status'),
    staleTime: 15 * 1000,
  })

  const startMutation = useMutation({
    mutationFn: () => api.post<UploadStartResult>('/settings/start-uploading'),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['settings', 'upload-status'] })
      alert(res.note || `${res.triggered} video upload queue me.`)
    },
    onError: (e: any) => alert(`Start failed: ${e?.message || e}`),
  })

  const pauseMutation = useMutation({
    mutationFn: () => api.post<UploadStatus>('/settings/upload-pause', { paused: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings', 'upload-status'] }),
    onError: (e: any) => alert(`Pause failed: ${e?.message || e}`),
  })

  const paused = data?.paused ?? null
  const busy = startMutation.isPending || pauseMutation.isPending

  return (
    <div className="card upload-gate-card" style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 12, padding: '14px 18px', marginBottom: 18,
      border: paused ? '1px solid #ef4444' : '1px solid #10b981',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <ShieldCheck size={26} color={paused ? '#ef4444' : '#10b981'} />
        <div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>
            Online Uploading:{' '}
            {paused === null ? '…' : paused ? (
              <span style={{ color: '#ef4444' }}>PAUSED 🛑</span>
            ) : (
              <span style={{ color: '#10b981' }}>ACTIVE ✅</span>
            )}
          </div>
          <div style={{ fontSize: 12, opacity: 0.75 }}>
            {paused
              ? `Broken videos channel par na jaayein — isliye upload ruke hain. ${data?.pending_uploads ?? 0} video pending queue me.`
              : 'Automation live hai — videos unlisted-first upload ho rahi hain.'}
            {data?.version ? ` · v${data.version}` : ''}
          </div>
        </div>
      </div>
      {paused ? (
        <button
          className="btn btn-primary"
          disabled={busy}
          onClick={() => startMutation.mutate()}
          style={{ whiteSpace: 'nowrap' }}
        >
          {startMutation.isPending ? 'Starting…' : '▶ Start online uploading'}
        </button>
      ) : (
        <button
          className="btn"
          disabled={busy}
          onClick={() => pauseMutation.mutate()}
          style={{ whiteSpace: 'nowrap', border: '1px solid #ef4444', color: '#ef4444' }}
        >
          {pauseMutation.isPending ? 'Pausing…' : '⏸ Pause uploading'}
        </button>
      )}
    </div>
  )
}

export default function Dashboard() {
  const { data: metrics, isLoading } = useQuery({
    queryKey: ['metrics', 'overview'],
    queryFn: () => api.get<{
      channels: number
      videos: { total: number; by_status: Record<string, number>; published: number }
      jobs: { total: number; by_status: Record<string, number> }
      topics: { total: number; by_status: Record<string, number> }
    }>('/metrics/overview?days=30'),
  })

  const { data: pipeline } = useQuery({
    queryKey: ['metrics', 'pipeline'],
    queryFn: () => api.get<{
      total_pipelines: number
      success_rate_pct: number
      avg_stage_duration_seconds: Record<string, number>
    }>('/metrics/pipeline-performance?days=30'),
  })

  // Render/Cloudflare/GitHub status + quota — 5 min cached (backend pe bhi
  // 10 min cache hai, isliye yahan staleTime lamba).
  const { data: platform } = useQuery({
    queryKey: ['metrics', 'platform'],
    queryFn: () => api.get<PlatformStatus>('/metrics/platform'),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })

  const { data: cost } = useQuery({
    queryKey: ['metrics', 'cost'],
    queryFn: () => api.get<{
      videos_produced: number
      estimated_openai_tokens: number
      estimated_tts_minutes: number
      estimated_cost_usd: { script_generation: number; text_to_speech: number; total: number }
    }>('/metrics/cost-estimate?days=30'),
  })

  const { data: channels } = useQuery({
    queryKey: ['channels'],
    queryFn: () => api.get<Channel[]>('/channels'),
  })

  // Mock chart data for demo
  const viewsData = Array.from({ length: 30 }, (_, i) => ({
    date: format(subDays(new Date(), 29 - i), 'MMM dd'),
    views: Math.floor(Math.random() * 5000) + 1000,
    revenue: Math.random() * 50 + 5,
  }))

  const statusData = metrics?.videos.by_status
    ? Object.entries(metrics.videos.by_status).map(([status, count]) => ({ status, count }))
    : [
        { status: 'Draft', count: 5 },
        { status: 'Scripted', count: 3 },
        { status: 'Published', count: 12 },
        { status: 'Failed', count: 1 },
      ]

  const jobStatusData = metrics?.jobs.by_status
    ? Object.entries(metrics.jobs.by_status).map(([status, count]) => ({ status, count }))
    : [
        { status: 'Completed', count: 45 },
        { status: 'Running', count: 3 },
        { status: 'Failed', count: 2 },
        { status: 'Pending', count: 5 },
      ]

  if (isLoading) return <HudLoader text="Loading dashboard" />

  return (
    <div className="page">
      <div className="page-header">
        <h1>Dashboard</h1>
        <p>Overview of your YouTube automation pipeline</p>
      </div>

      {/* 2.2.36 Task 3 — global upload kill-switch + Start button */}
      <UploadGateCard />

      <div className="stats-grid">
        <StatCard
          title="Channels"
          value={metrics?.channels || 0}
          icon={Youtube}
          color="#ff0000"
        />
        <StatCard
          title="Videos Published"
          value={metrics?.videos.published || 0}
          change={{ value: 12, label: 'vs last month' }}
          icon={Video}
          color="#3b82f6"
        />
        <StatCard
          title="Active Jobs"
          value={metrics?.jobs.by_status?.running || 0}
          icon={Briefcase}
          color="#10b981"
        />
        <StatCard
          title="Topics Queued"
          value={metrics?.topics.by_status?.discovered || 0}
          icon={FileText}
          color="#8b5cf6"
        />
        <StatCard
          title="Est. Cost (30d)"
          value={`$${cost?.estimated_cost_usd?.total.toFixed(2) || '0.00'}`}
          icon={DollarSign}
          color="#10b981"
        />
        <StatCard
          title="Pipeline Success Rate"
          value={`${pipeline?.success_rate_pct || 0}%`}
          icon={TrendingUp}
          color="#f59e0b"
        />
      </div>

      {/* Status row: Supabase + Neon + Render/Cloudflare/GitHub — sab ek
          line me (bagal-bagal), chhote cards. */}
      <div className="stats-grid status-grid">
        <SupabaseDbCard />
        <NeonDbCard />
        {platform?.services?.map((s) => <PlatformCard key={s.id} svc={s} />)}
      </div>

      <div className="monet-grid">
        {channels?.map((c) => <MonetizationCard key={c.id} channel={c} />)}
      </div>

      <div className="charts-grid">
        <ChartCard title="Views & Revenue (30 days)">
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={viewsData}>
              <defs>
                <linearGradient id="viewsGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" stroke="#9ca3af" tick={{ fontSize: 12 }} />
              <YAxis yAxisId="left" stroke="#9ca3af" tick={{ fontSize: 12 }} label={{ value: 'Views', angle: -90, position: 'insideLeft' }} />
              <YAxis yAxisId="right" orientation="right" stroke="#9ca3af" tick={{ fontSize: 12 }} label={{ value: 'Revenue ($)', angle: 90, position: 'insideRight' }} />
              <Tooltip
                labelFormatter={(v) => format(new Date(v), 'MMM dd, yyyy')}
                formatter={(value: number, name: string) => [value, name === 'views' ? 'Views' : 'Revenue ($)']}
              />
              <Area type="monotone" dataKey="views" yAxisId="left" stroke="#3b82f6" fillOpacity={1} fill="url(#viewsGradient)" />
              <Area type="monotone" dataKey="revenue" yAxisId="right" stroke="#10b981" fillOpacity={1} fill="url(#revenueGradient)" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Video Status Distribution">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={statusData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
                dataKey="count"
                nameKey="status"
                label={({ status, percent }) => `${status} ${(percent * 100).toFixed(0)}%`}
                labelLine={false}
              >
                {statusData.map((_, i) => (
                  <Cell key={`cell-${i}`} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value: number) => [value, 'Videos']} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Job Status (30 days)">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={jobStatusData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis type="number" stroke="#9ca3af" tick={{ fontSize: 12 }} />
              <YAxis type="category" dataKey="status" width={100} stroke="#9ca3af" tick={{ fontSize: 12 }} />
              <Tooltip formatter={(value: number) => [value, 'Jobs']} />
              <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Pipeline Stage Duration (avg)">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={pipeline?.avg_stage_duration_seconds ? Object.entries(pipeline.avg_stage_duration_seconds).map(([stage, seconds]) => ({ stage, seconds: Math.round(seconds) })) : [
              { stage: 'Research', seconds: 45 },
              { stage: 'Script', seconds: 120 },
              { stage: 'Voice', seconds: 60 },
              { stage: 'Visual', seconds: 30 },
              { stage: 'Assemble', seconds: 180 },
              { stage: 'Metadata', seconds: 25 },
              { stage: 'Compliance', seconds: 15 },
              { stage: 'Publish', seconds: 40 },
            ]} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis type="number" stroke="#9ca3af" tick={{ fontSize: 12 }} label={{ value: 'Seconds', position: 'insideTop' }} />
              <YAxis type="category" dataKey="stage" width={100} stroke="#9ca3af" tick={{ fontSize: 12 }} />
              <Tooltip formatter={(value: number) => [value, 'Seconds']} />
              <Bar dataKey="seconds" fill="#8b5cf6" radius={[4, 0, 0, 4]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="recent-activity">
        <h2>Recent Activity</h2>
        <div className="activity-list">
          <div className="activity-item">
            <div className="activity-icon" style={{ backgroundColor: '#3b82f615', color: '#3b82f6' }}>
              <Video size={18} />
            </div>
            <div className="activity-content">
              <div className="activity-title">Video published: "FastAPI vs Django in 2024"</div>
              <div className="activity-meta">Channel: Tech Automation Demo • 2 hours ago</div>
            </div>
            <span className="activity-badge success">Published</span>
          </div>
          <div className="activity-item">
            <div className="activity-icon" style={{ backgroundColor: '#10b98115', color: '#10b981' }}>
              <FileText size={18} />
            </div>
            <div className="activity-content">
              <div className="activity-title">Topic discovered: "Building AI Agents with LangGraph"</div>
              <div className="activity-meta">Source: Google Trends (US) • 4 hours ago</div>
            </div>
            <span className="activity-badge info">Discovered</span>
          </div>
          <div className="activity-item">
            <div className="activity-icon" style={{ backgroundColor: '#f59e0b15', color: '#f59e0b' }}>
              <Briefcase size={18} />
            </div>
            <div className="activity-content">
              <div className="activity-title">Pipeline completed: Full pipeline for "React Server Components"</div>
              <div className="activity-meta">Duration: 8m 32s • 6 hours ago</div>
            </div>
            <span className="activity-badge success">Completed</span>
          </div>
          <div className="activity-item">
            <div className="activity-icon" style={{ backgroundColor: '#ef444415', color: '#ef4444' }}>
              <ShieldCheck size={18} />
            </div>
            <div className="activity-content">
              <div className="activity-title">Policy check failed: "Python 3.14 Features" - Copyright risk</div>
              <div className="activity-meta">Blocker: Unlicensed code snippets detected • 8 hours ago</div>
            </div>
            <span className="activity-badge error">Blocked</span>
          </div>
        </div>
      </div>
    </div>
  )
}
